export type OnlineMessageRating = 'cold' | 'warm' | 'hot';

type OnlineMessageTrackingRule = {
  rating?: OnlineMessageRating;
  type: string;
};

const TRACKING_RULES: Record<string, OnlineMessageTrackingRule> = {
  '/contact-us/cdmo-inquiry': { rating: 'hot', type: 'from_cdmoinquiry' },
  '/contact-us/partnership': { rating: 'cold', type: 'form_partnership' },
  '/contact-us/careers': { rating: 'cold', type: 'from_careers' },
  '/contact-us/investors': { rating: 'cold', type: 'from_investors' },
  '/resources/resource-library': { type: 'from_resourcelibrary' },
  '/resources/webinars': { type: 'from_webinars' },
  '/resource-library': { type: 'from_resourcelibrary' },
  '/webinars': { type: 'from_webinars' },
};

const getTrackingRule = (landingUrl: string) => {
  const exactRule = TRACKING_RULES[landingUrl];
  if (exactRule) return exactRule;

  const parentPath = Object.keys(TRACKING_RULES)
    .filter((path) => landingUrl.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];
  return parentPath ? TRACKING_RULES[parentPath] : undefined;
};

const PREVIOUS_URL_STORAGE_KEY = 'onlineMessagePreviousUrls';
const LEGACY_PREVIOUS_URL_STORAGE_KEY = 'onlineMessagePreviousUrl';
const MAX_PREVIOUS_URLS = 2;
const DRAFT_LANDING_URLS = new Set([
  '/contact-us/cdmo-inquiry',
  '/contact-us/partnership',
  '/contact-us/careers',
  '/contact-us/investors',
]);

const normalizeLandingUrl = (pathname: string) => {
  let path = pathname;
  try {
    path = new URL(pathname, window.location.origin).pathname;
  } catch {
    path = pathname.split(/[?#]/, 1)[0];
  }

  const withoutLanguage = path.replace(/^\/[a-z]{2}(?:-[A-Z]{2})?(?=\/|$)/, '');
  const normalized = `/${withoutLanguage}`.replace(/\/{2,}/g, '/').replace(/\/$/, '');
  return normalized || '/';
};

export const captureOnlineMessagePreviousUrl = (previousUrl?: string) => {
  if (typeof window === 'undefined') return;

  if (!previousUrl) return;

  const storedUrls = getStoredPreviousUrls();
  const previousUrls = [...storedUrls, previousUrl].slice(-MAX_PREVIOUS_URLS);
  window.sessionStorage.setItem(PREVIOUS_URL_STORAGE_KEY, JSON.stringify(previousUrls));
  window.sessionStorage.removeItem(LEGACY_PREVIOUS_URL_STORAGE_KEY);
};

const getStoredPreviousUrls = () => {
  if (typeof window === 'undefined') return [] as string[];

  const storedValue = window.sessionStorage.getItem(PREVIOUS_URL_STORAGE_KEY);
  if (storedValue) {
    try {
      const urls = JSON.parse(storedValue);
      if (Array.isArray(urls)) {
        return urls.filter((url): url is string => typeof url === 'string');
      }
    } catch {
      // Ignore malformed session storage values.
    }
  }

  const legacyUrl = window.sessionStorage.getItem(LEGACY_PREVIOUS_URL_STORAGE_KEY);
  return legacyUrl ? [legacyUrl] : [];
};

const getPreviousUrl = () => {
  if (typeof window === 'undefined') return '';

  const previousUrls = getStoredPreviousUrls();
  const latestUrl = previousUrls.at(-1);
  if (latestUrl && normalizeLandingUrl(latestUrl) === '/contact-us') {
    return previousUrls.at(-2) || document.referrer || '';
  }

  return latestUrl || document.referrer || '';
};

const getResourceRating = (formData: Record<string, unknown> = {}): OnlineMessageRating => {
  const rawStartTime =
    formData.timeline ?? formData.startTime ?? formData.start_time ?? formData.startDate ?? '';
  const startTimeValue =
    rawStartTime && typeof rawStartTime === 'object'
      ? ((rawStartTime as { value?: unknown; text?: unknown; phrase?: unknown }).value ??
        (rawStartTime as { text?: unknown }).text ??
        (rawStartTime as { phrase?: unknown }).phrase ??
        '')
      : rawStartTime;
  const startTime = String(startTimeValue).trim().toLowerCase();
  const normalizedStartTime = startTime.replace(/[\s_/]+/g, '-');

  if (
    normalizedStartTime === 'immediately' ||
    normalizedStartTime === 'asap' ||
    normalizedStartTime === 'immediately-asap' ||
    /immediately.*asap|asap.*immediately/.test(normalizedStartTime) ||
    /(?:start[-_ ]?now|start[-_ ]?immediately|^now$)/.test(normalizedStartTime)
  ) {
    return 'hot';
  }

  if (
    /^within-(?:3|6)-months?$/.test(normalizedStartTime) ||
    /^(?:within-)?(?:6个月内|六个月内)$/.test(startTime)
  ) {
    return 'warm';
  }

  return 'cold';
};

export const getOnlineMessageTracking = (formData: Record<string, unknown> = {}) => {
  const landingPath = getOnlineMessageLandingUrl();
  const landingUrl = `${landingPath}${typeof window !== 'undefined' ? window.location.search : ''}`;

  const rule = getTrackingRule(landingPath);
  const hasTimeline = Object.prototype.hasOwnProperty.call(formData, 'timeline');
  const rating = hasTimeline ? getResourceRating(formData) : (rule?.rating ?? 'cold');

  const previousUrl = getPreviousUrl();
  return {
    landingUrl,
    rating,
    type: rule?.type,
    engagementSource: previousUrl,
    previousUrl,
  };
};

export const getOnlineMessageLandingUrl = () =>
  normalizeLandingUrl(typeof window !== 'undefined' ? window.location.pathname : '/');

export const isOnlineMessageDraftPage = () => DRAFT_LANDING_URLS.has(getOnlineMessageLandingUrl());
